// ============================================================
// useSessions — OLIVIUM Presence.
//
// Demo: returns three plausible sessions with one suspicious.
// Live: queries olv_sessions ordered by last_seen_at desc and
//       refetches whenever olv_sessions changes via Realtime.
// ============================================================

import { useEffect, useState } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { getRuntime } from './_mode'
import type { OlvSession } from './types'
import { captureHookFailure, recordHookOutcome, withSpan } from '../observability/sentry'

function generateDemoSessions(): OlvSession[] {
  return [
    {
      id: 'demo-session-1',
      user_id: 'jordan',
      device_label: 'MacBook Pro',
      device_fingerprint: 'fp-mbp-001',
      ip_address: '10.0.0.12',
      user_agent: 'Chrome 132 / macOS',
      location_hint: 'Atlanta, GA',
      status: 'active',
      started_at: new Date(Date.now() - 3 * 3600 * 1000).toISOString(),
      last_seen_at: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
      expires_at: new Date(Date.now() + 8 * 3600 * 1000).toISOString(),
    },
    {
      id: 'demo-session-2',
      user_id: 'console-admin',
      device_label: 'iPhone 15 Pro',
      device_fingerprint: 'fp-iphone-002',
      ip_address: '10.0.0.13',
      user_agent: 'Mobile Safari / iOS 18',
      location_hint: 'Atlanta, GA',
      status: 'active',
      started_at: new Date(Date.now() - 90 * 60 * 1000).toISOString(),
      last_seen_at: new Date(Date.now() - 6 * 60 * 1000).toISOString(),
      expires_at: new Date(Date.now() + 8 * 3600 * 1000).toISOString(),
    },
    {
      id: 'demo-session-3',
      user_id: 'law-pocket-ops',
      device_label: 'Windows Desktop',
      device_fingerprint: 'fp-win-003',
      ip_address: '10.0.0.14',
      user_agent: 'Edge 132 / Windows 11',
      location_hint: 'Indianapolis, IN',
      status: 'suspicious',
      started_at: new Date(Date.now() - 5 * 3600 * 1000).toISOString(),
      last_seen_at: new Date(Date.now() - 14 * 60 * 1000).toISOString(),
      expires_at: new Date(Date.now() + 4 * 3600 * 1000).toISOString(),
    },
  ]
}

export function useSessions() {
  const [sessions, setSessions] = useState<OlvSession[]>([])
  const [loading, setLoading] = useState(true)
  const [authRequired, setAuthRequired] = useState(false)

  useEffect(() => {
    let cancelled = false
    let channel: RealtimeChannel | null = null
    const { live, supabase } = getRuntime()

    async function load() {
      if (!live || !supabase) {
        const demo = generateDemoSessions()
        if (!cancelled) {
          setSessions(demo)
          setAuthRequired(false)
          setLoading(false)
        }
        recordHookOutcome('useSessions', 'demo_fallback', { rows: demo.length })
        return
      }

      const startedAt = Date.now()
      try {
        const { data: authData } = await supabase.auth.getSession()
        if (!authData.session) {
          if (!cancelled) {
            setSessions([])
            setAuthRequired(true)
            setLoading(false)
          }
          return
        }

        const { data, error } = await withSpan('olv_sessions.select', 'db.query', async () =>
          supabase
            .from('olv_sessions')
            .select('*')
            .order('last_seen_at', { ascending: false })
        )

        if (cancelled) return
        const durationMs = Date.now() - startedAt

        if (error) {
          const message = error.message || ''
          const missing =
            /relation .* does not exist/i.test(message) ||
            /could not find the table/i.test(message) ||
            error.code === '42P01'
          setSessions([])
          captureHookFailure(
            'useSessions',
            missing ? 'missing_dependency' : 'live_error',
            error,
            { table: 'olv_sessions', durationMs, message }
          )
        } else {
          const rows = (data as OlvSession[]) || []
          setSessions(rows)
          setAuthRequired(false)
          recordHookOutcome('useSessions', 'live_success', {
            table: 'olv_sessions',
            rows: rows.length,
            durationMs,
          })
        }
      } catch (err) {
        if (cancelled) return
        setSessions([])
        captureHookFailure('useSessions', 'live_error', err, { table: 'olv_sessions' })
      }
      setLoading(false)

      try {
        channel = supabase
          .channel('square-shell-sessions')
          .on(
            'postgres_changes' as any,
            { event: '*', schema: 'public', table: 'olv_sessions' },
            async () => {
              try {
                const { data: next } = await withSpan('olv_sessions.refresh', 'db.query', async () =>
                  supabase
                    .from('olv_sessions')
                    .select('*')
                    .order('last_seen_at', { ascending: false })
                )
                if (!cancelled) setSessions((next as OlvSession[]) || [])
              } catch (err) {
                captureHookFailure('useSessions', 'realtime_failure', err, {
                  table: 'olv_sessions',
                  message: 'refresh failed',
                })
              }
            }
          )
          .subscribe((status: string) => {
            if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
              captureHookFailure(
                'useSessions',
                'realtime_failure',
                new Error(`sessions realtime ${status}`),
                { table: 'olv_sessions', message: status }
              )
            }
          })
      } catch (err) {
        captureHookFailure('useSessions', 'realtime_failure', err, { table: 'olv_sessions' })
      }
    }

    load()
    return () => {
      cancelled = true
      if (channel && supabase) supabase.removeChannel(channel)
    }
  }, [])

  return { sessions, loading, authRequired }
}
